import React, { useState, useRef, useEffect, useCallback } from "react";
import groupImg from "../../../../../../assets/Group.png";
import dependableImg from "../../../../../../assets/dependable.png";
import complianceImg from "../../../../../../assets/compliance.png";
import sociabilityImg from "../../../../../../assets/sociability.png";
import independenceImg from "../../../../../../assets/independence.png";
import growthImg from "../../../../../../assets/growth.png";
import sliderLineImg from "../../../../../../assets/slider.png";
import knobImg from "../../../../../../assets/slidingknob.png";
import RecommendedPositionCard from "./RecommendedPositionCard";


const LEVELS = [
  {
    title: "Survival",
    description:
      "Lacks basic job skills or new to company or in high stress and near burnout. They just want the work done exactly as asked. They don't like surprises and won't take decisions unless someone senior confirms it. You mostly follow instructions and keep your head down around them.",
    card: {
      header: "YOUR RECOMMENDED POSITIONING",
      name: "The Dependable Hero",
      tagline: "Build trust through steady presence and risk reduction.",
      bullets: [
        "Maintain a steady, reliable presence",
        "Focus on risk reduction strategies",
        "Emphasize problem avoidance",
        "Provide clear, consistent communication",
      ],
      img: dependableImg,
      alt: "Dependable Icon",
    },
  },
  {
    title: "Safety",
    description:
      "Not a risk taker, predictability, hates change of plans. They're very particular about process, formats, and approvals. Everything has to be correct and documented, even if it slows things down. You feel safer, but also a bit constrained working with them.",
    card: {
      header: "YOUR RECOMMENDED POSITIONING",
      name: "The Compliance Champion",
      tagline: "Demonstrate loyalty, trust, and clear communication",
      bullets: [
        "Show unwavering loyalty to team and company",
        "Build trust through consistent actions",
        "Communicate clearly and transparently",
        "Follow and uphold policies with integrity",
      ],
      img: complianceImg,
      alt: "Compliance Icon",
    },
  },
  {
    title: "Connection",
    description:
      "Seeking positive relationships, inclusion, recognition from peers/superior. They're nice and approachable and genuinely care about the team. They try hard to keep everyone comfortable and avoid conflict. Sometimes things drag because they don't want to upset anyone.",
    card: {
      header: "YOUR RECOMMENDED POSITIONING",
      name: "The Connection Catalyst",
      tagline: "Foster positive connections and enhance reputation",
      bullets: [
        "Foster positive, authentic communication",
        "Build and maintain a strong network",
        "Enhance team and organizational reputation",
        "Create inclusive environments",
      ],
      img: sociabilityImg,
      alt: "Sociability Icon",
    },
  },
  {
    title: "Esteem Needs",
    description:
      "Seeking respect, freedom, recognition. They like being in control and owning the outcome. They trust themselves more than the system and prefer to drive things to get reward. You know things will move, but only when they get credit.",
    card: {
      header: "YOUR RECOMMENDED POSITIONING",
      name: "The Initiative Driver",
      tagline: "Demonstrate competence and advocate for others",
      bullets: [
        "Give team members independence and autonomy",
        "Demonstrate exceptional competence",
        "Advocate for team and individual growth",
        "Empower others to excel",
      ],
      img: independenceImg,
      alt: "Independence Icon",
    },
  },
  {
    title: "Self-Actualization",
    description:
      "Wants to build legacy, creativity, problem solving, or life beyond work. They're always pushing for better ways of doing things. They question old systems and want change fast. Working with them is exciting, but can feel intense if you like stability.",
    card: {
      header: "YOUR RECOMMENDED POSITIONING",
      name: "The Innovative Builder",
      tagline: "Drive strategic thinking and authentic innovation",
      bullets: [
        "Cultivate a growth mindset",
        "Drive innovation and creative problem solving",
        "Think strategically about long-term impact",
        "Lead with authenticity and purpose",
      ],
      img: growthImg,
      alt: "Growth Icon",
    },
  },
];



const sliderWidth = 680;
const knobWidth = 60;
const KNOB_OFFSET_PX = 24;
const NUM_LEVELS = 5;
const STEP = (sliderWidth - knobWidth - 2 * KNOB_OFFSET_PX) / (NUM_LEVELS - 1);


export default function StakeholderNeedsSliderStep({ onLevelSelect, selectedLevel, onNextStep }) {
  const initialIndex =
    typeof selectedLevel?.index === "number" &&
    selectedLevel.index >= 0 &&
    selectedLevel.index < NUM_LEVELS
      ? selectedLevel.index
      : 0;


  const [levelIdx, setLevelIdx] = useState(initialIndex);
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef(null);


  useEffect(() => {
    if (
      typeof selectedLevel?.index === "number" &&
      selectedLevel.index >= 0 &&
      selectedLevel.index < NUM_LEVELS
    ) {
      setLevelIdx(selectedLevel.index);
    }
  }, [selectedLevel]);


  function onKnobDown(e) {
    setIsDragging(true);
    document.body.style.userSelect = "none";
    e.preventDefault();
  }


  function onKnobUp() {
    setIsDragging(false);
    document.body.style.userSelect = "";
  }

  // function onKnobMove(e) {
  //   if (!isDragging || !sliderRef.current) return;
  //   const rect = sliderRef.current.getBoundingClientRect();
  //   const clientX = e.type.startsWith("touch")
  //     ? e.touches[0]?.clientX ?? e.changedTouches[0]?.clientX
  //     : e.clientX;
  //   let px = clientX - rect.left - knobWidth / 2;
  //   px = Math.max(0, Math.min(sliderWidth - knobWidth, px));
  //   setLevelIdx(
  //     Math.max(0, Math.min(NUM_LEVELS - 1, Math.round(px / STEP)))
  //   );
  // }

  const onKnobMove = useCallback((e) => {
    if (!isDragging || !sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();

    const clientX = e.type.startsWith("touch")
      ? e.touches[0]?.clientX ?? e.changedTouches[0]?.clientX
      : e.clientX;

    let px = clientX - rect.left - knobWidth / 2;
    px = Math.max(0, Math.min(sliderWidth - knobWidth, px));

    setLevelIdx(
      Math.max(0, Math.min(NUM_LEVELS - 1, Math.round(px / STEP)))
    );
  }, [
    isDragging,
  ]);


  useEffect(() => {
    function handleMove(e) {
      onKnobMove(e);
    }
    function handleUp() {
      onKnobUp();
    }
    if (isDragging) {
      window.addEventListener("mousemove", handleMove);
      window.addEventListener("mouseup", handleUp);
      window.addEventListener("touchmove", handleMove, { passive: false });
      window.addEventListener("touchend", handleUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleUp);
    };
  }, [isDragging, onKnobMove]);


  const handleContinue = () => {
    if (!onLevelSelect) return;
    const level = { index: levelIdx, title: LEVELS[levelIdx].title };
    const card = LEVELS[levelIdx].card;
    onLevelSelect({ level, card });
    if (onNextStep) onNextStep();
  };


  return (
    <div className="w-full h-full bg-white flex flex-col">
      {/* ✅ MAIN HEADING - REDUCED FONT */}
      <div className="flex flex-col items-left mb-20">
        <h1
          className="text-[26px] font-[500] leading-[135%] text-[#323232] mb-2"  // ✅ REDUCED from 32px to 26px
          style={{ fontFamily: "Gotham, sans-serif" }}
        >
          Stakeholder Needs &rarr; Recommended Positioning
        </h1>
        <div className="text-center text-[#454545] font-[400] text-[14px] leading-[165%] max-w-[700px]">  {/* ✅ REDUCED from 16px to 14px */}
        What level are your stakeholders psychologically? You can accordingly align yourself to give what they hunger for.
        </div>
      </div>
      
      <div className="w-full flex flex-row justify-center items-start gap-14">
        {/* LEFT: Level/Slider & Table */}
        <div className="flex flex-col min-w-[510px] flex-1">
          {/* ✅ LEVEL TITLE - REDUCED FONT */}
          <h2
            className="text-[18px] font-semibold text-[#16A5A5] mb-1"  // ✅ REDUCED from 22px to 18px
            style={{ fontFamily: "Gotham, sans-serif", letterSpacing: "0" }}
          >
            {LEVELS[levelIdx].title}
          </h2>
          
          {/* ✅ DESCRIPTION - REDUCED FONT */}
          <div
            className="text-[#323232] font-[400] text-[13px] leading-[150%] mb-4 max-w-[420px]"  // ✅ REDUCED from 15px to 13px
            style={{ fontFamily: "Gotham, sans-serif", letterSpacing: "0" }}
          >
            {LEVELS[levelIdx].description}
          </div>
          
          <div className="flex flex-col items-start" ref={sliderRef}>
            <div
              className="relative"
              style={{ width: sliderWidth, height: "82.5px", marginBottom: "16px" }}
            >
              <img
                src={sliderLineImg}
                alt="slider-bar"
                style={{
                  width: sliderWidth,
                  height: "6px",
                  position: "absolute",
                  left: 0,
                  top: "40px",
                  zIndex: 1,
                  opacity: 1,
                }}
                draggable={false}
              />
              <img
                src={knobImg}
                alt="slider-knob"
                className={isDragging ? "cursor-grabbing" : "cursor-pointer"}
                style={{
                  position: "absolute",
                  top: "32px",
                  left: KNOB_OFFSET_PX + levelIdx * STEP,
                  width: knobWidth,
                  zIndex: 2,
                  opacity: 1,
                  transition: isDragging
                    ? "none"
                    : "left 0.3s cubic-bezier(.4,1.7,.5,1)",
                  userSelect: "none",
                  cursor: isDragging ? "grabbing" : "pointer",
                  touchAction: "none",
                }}
                draggable={false}
                onMouseDown={onKnobDown}
                onTouchStart={onKnobDown}
              />
              
              {/* ✅ LEVEL NUMBERS - REDUCED FONT */}
              <div
                className="absolute left-0 w-full flex flex-row justify-between"
                style={{ top: 60 }}
              >
                {[1, 2, 3, 4, 5].map((lvl, idx) => (
                  <div
                    key={lvl}
                    className="flex flex-col items-center"
                    style={{ width: 110 }}
                  >
                    {levelIdx === idx ? (
                      <>
                        <span
                          className="text-[#141414] font-medium text-[32px] uppercase block leading-[135%]"  // ✅ REDUCED from 38px to 32px
                          style={{
                            fontFamily: "Gotham, sans-serif",
                            letterSpacing: 0,
                            textAlign: "center",
                          }}
                        >
                          {`0${lvl}`}
                        </span>
                        <span
                          className="text-[#141414] font-medium text-[13px] uppercase block leading-[135%]"  // ✅ REDUCED from 15px to 13px
                          style={{
                            fontFamily: "Gotham, sans-serif",
                            letterSpacing: 0,
                            textAlign: "center",
                          }}
                        >
                          LEVEL
                        </span>
                      </>
                    ) : (
                      <span
                        className="text-[#9F9E9E] font-medium text-[16px] uppercase block leading-[135%] flex gap-1"  // ✅ REDUCED from 19px to 16px
                        style={{
                          fontFamily: "Gotham, sans-serif",
                          letterSpacing: 0,
                          textAlign: "center",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {`0${lvl}`} <span className="ml-1">LEVEL</span>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="relative mt-16 select-none">
            <img
              src={groupImg}
              alt="people-table"
              className="absolute top-0 left-12 pointer-events-none user-drag-none"
              style={{
                width: "650px",
                height: "300px",
                opacity: 1,
                pointerEvents: "none",
                userSelect: "none",
              }}
              draggable={false}
            />


            <div
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                width: "100%",
                height: "300px",
                pointerEvents: "none",
                background:
                  "linear-gradient(180deg,rgba(255,255,255,0) 0%, #FFFFFF 100%)",
              }}
            />


            {/* ✅ BUTTON TEXT - REDUCED FONT */}
            <button
              type="button"
              onClick={handleContinue}
              className="absolute flex items-center justify-between font-medium bg-black text-white text-[19px] rounded-[20.66px] shadow-md"  // ✅ REDUCED from 22px to 19px
              style={{
                width: "250.57px",
                height: "70.94px",
                top: "106.73px",
                left: "220px",
                padding: "22.72px 20.66px",
                fontFamily: "Gotham, sans-serif",
                letterSpacing: 0,
                opacity: 1,
                zIndex: 3,
              }}
            >
              Make Selection
              <span className="ml-4 flex items-center">
                <svg width={22} height={22} viewBox="0 0 22 22" fill="none">
                  <circle cx="11" cy="11" r="11" fill="#fff" />
                  <path
                    d="M9 7l4 4-4 4"
                    stroke="#151A1F"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </button>
          </div>
        </div>
        
        {/* RIGHT: Recommendation Card */}
        <div className="flex flex-col">
          <RecommendedPositionCard card={LEVELS[levelIdx].card} />
        </div>
      </div>
    </div>
  );
}


// import React, { useState, useRef, useEffect } from "react";
// import groupImg from "../../../../../../assets/Group.png";
// import dependableImg from "../../../../../../assets/dependable.png";
// import complianceImg from "../../../../../../assets/compliance.png";
// import sociabilityImg from "../../../../../../assets/sociability.png";
// import independenceImg from "../../../../../../assets/independence.png";
// import growthImg from "../../../../../../assets/growth.png";
// import sliderLineImg from "../../../../../../assets/slider.png";
// import knobImg from "../../../../../../assets/slidingknob.png";
// import RecommendedPositionCard from "./RecommendedPositionCard";

// const LEVELS = [
//   {
//     title: "Survival",
//     description:
//       "Lacks basic job skills or new to company or in high stress and near burnout. They just want the work done exactly as asked. They don't like surprises and won't take decisions unless someone senior confirms it. You mostly follow instructions and keep your head down around them.",
//     card: {
//       header: "YOUR RECOMMENDED POSITIONING",
//       name: "The Dependable Hero",
//       tagline: "Build trust through steady presence and risk reduction.",
//       bullets: [
//         "Maintain a steady, reliable presence",
//         "Focus on risk reduction strategies",
//         "Emphasize problem avoidance",
//         "Provide clear, consistent communication",
//       ],
//       img: dependableImg,
//       alt: "Dependable Icon",
//     },
//   },
//   {
//     title: "Safety",
//     description:
//       "Not a risk taker, predictability, hates change of plans. They're very particular about process, formats, and approvals. Everything has to be correct and documented, even if it slows things down. You feel safer, but also a bit constrained working with them.",
//     card: {
//       header: "YOUR RECOMMENDED POSITIONING",
//       name: "The Compliance Champion",
//       tagline: "Demonstrate loyalty, trust, and clear communication",
//       bullets: [
//         "Show unwavering loyalty to team and company",
//         "Build trust through consistent actions",
//         "Communicate clearly and transparently",
//         "Follow and uphold policies with integrity",
//       ],
//       img: complianceImg,
//       alt: "Compliance Icon",
//     },
//   },
//   {
//     title: "Connection",
//     description:
//       "Seeking positive relationships, inclusion, recognition from peers/superior. They're nice and approachable and genuinely care about the team. They try hard to keep everyone comfortable and avoid conflict. Sometimes things drag because they don't want to upset anyone.",
//     card: {
//       header: "YOUR RECOMMENDED POSITIONING",
//       name: "The Connection Catalyst",
//       tagline: "Foster positive connections and enhance reputation",
//       bullets: [
//         "Foster positive, authentic communication",
//         "Build and maintain a strong network",
//         "Enhance team and organizational reputation",
//         "Create inclusive environments",
//       ],
//       img: sociabilityImg,
//       alt: "Sociability Icon",
//     },
//   },
//   {
//     title: "Esteem Needs",
//     description:
//       "Seeking respect, freedom, recognition. They like being in control and owning the outcome. They trust themselves more than the system and prefer to drive things to get reward. You know things will move, but only when they get credit.",
//     card: {
//       header: "YOUR RECOMMENDED POSITIONING",
//       name: "The Initiative Driver",
//       tagline: "Demonstrate competence and advocate for others",
//       bullets: [
//         "Give team members independence and autonomy",
//         "Demonstrate exceptional competence",
//         "Advocate for team and individual growth",
//         "Empower others to excel",
//       ],
//       img: independenceImg,
//       alt: "Independence Icon",
//     },
//   },
//   {
//     title: "Self-Actualization",
//     description:
//       "Wants to build legacy, creativity, problem solving, or life beyond work. They're always pushing for better ways of doing things. They question old systems and want change fast. Working with them is exciting, but can feel intense if you like stability.",
//     card: {
//       header: "YOUR RECOMMENDED POSITIONING",
//       name: "The Innovative Builder",
//       tagline: "Drive strategic thinking and authentic innovation",
//       bullets: [
//         "Cultivate a growth mindset",
//         "Drive innovation and creative problem solving",
//         "Think strategically about long-term impact",
//         "Lead with authenticity and purpose",
//       ],
//       img: growthImg,
//       alt: "Growth Icon",
//     },
//   },
// ];


// const sliderWidth = 680;
// const knobWidth = 60;
// const KNOB_OFFSET_PX = 24;
// const NUM_LEVELS = 5;
// const STEP = (sliderWidth - knobWidth - 2 * KNOB_OFFSET_PX) / (NUM_LEVELS - 1);

// export default function StakeholderNeedsSliderStep({ onLevelSelect, selectedLevel, onNextStep }) {
//   // initialise from saved level if available
//   const initialIndex =
//     typeof selectedLevel?.index === "number" &&
//     selectedLevel.index >= 0 &&
//     selectedLevel.index < NUM_LEVELS
//       ? selectedLevel.index
//       : 0;

//   const [levelIdx, setLevelIdx] = useState(initialIndex);
//   const [isDragging, setIsDragging] = useState(false);
//   const sliderRef = useRef(null);

//   // keep levelIdx in sync when selectedLevel changes (e.g. after load)
//   useEffect(() => {
//     if (
//       typeof selectedLevel?.index === "number" &&
//       selectedLevel.index >= 0 &&
//       selectedLevel.index < NUM_LEVELS
//     ) {
//       setLevelIdx(selectedLevel.index);
//     }
//   }, [selectedLevel]);

//   function onKnobDown(e) {
//     setIsDragging(true);
//     document.body.style.userSelect = "none";
//     e.preventDefault();
//   }

//   function onKnobUp() {
//     setIsDragging(false);
//     document.body.style.userSelect = "";
//   }

//   function onKnobMove(e) {
//     if (!isDragging || !sliderRef.current) return;
//     const rect = sliderRef.current.getBoundingClientRect();
//     const clientX = e.type.startsWith("touch")
//       ? e.touches[0]?.clientX ?? e.changedTouches[0]?.clientX
//       : e.clientX;
//     let px = clientX - rect.left - knobWidth / 2;
//     px = Math.max(0, Math.min(sliderWidth - knobWidth, px));
//     setLevelIdx(
//       Math.max(0, Math.min(NUM_LEVELS - 1, Math.round(px / STEP)))
//     );
//   }

//   useEffect(() => {
//     function handleMove(e) {
//       onKnobMove(e);
//     }
//     function handleUp() {
//       onKnobUp();
//     }
//     if (isDragging) {
//       window.addEventListener("mousemove", handleMove);
//       window.addEventListener("mouseup", handleUp);
//       window.addEventListener("touchmove", handleMove, { passive: false });
//       window.addEventListener("touchend", handleUp);
//     }
//     return () => {
//       window.removeEventListener("mousemove", handleMove);
//       window.removeEventListener("mouseup", handleUp);
//       window.removeEventListener("touchmove", handleMove);
//       window.removeEventListener("touchend", handleUp);
//     };
//   }, [isDragging]);

//   const handleContinue = () => {
//     if (!onLevelSelect) return;
//     const level = { index: levelIdx, title: LEVELS[levelIdx].title };
//     const card = LEVELS[levelIdx].card;
//     onLevelSelect({ level, card });
//     if (onNextStep) onNextStep();
//   };

//   return (
//     <div className="w-full h-full bg-white flex flex-col">
//       <div className="flex flex-col items-center mb-20">
//         <h1
//           className="text-[32px] font-[500] leading-[135%] text-[#323232] text-center mb-2"
//           style={{ fontFamily: "Gotham, sans-serif" }}
//         >
//           Stakeholder Needs &rarr; Recommended Positioning
//         </h1>
//         <div className="text-center text-[#454545] font-[400] text-[16px] leading-[165%] max-w-[700px]">
//         What level are your stakeholders psychologically? You can accordingly align yourself to give what they hunger for.
//         </div>
//       </div>
//       <div className="w-full flex flex-row justify-center items-start gap-14">
//         {/* LEFT: Level/Slider & Table */}
//         <div className="flex flex-col min-w-[510px] flex-1">
//           <h2
//             className="text-[22px] font-semibold text-[#16A5A5] mb-1"
//             style={{ fontFamily: "Gotham, sans-serif", letterSpacing: "0" }}
//           >
//             {LEVELS[levelIdx].title}
//           </h2>
//           <div
//             className="text-[#323232] font-[400] text-[15px] leading-[150%] mb-4 max-w-[420px]"
//             style={{ fontFamily: "Gotham, sans-serif", letterSpacing: "0" }}
//           >
//             {LEVELS[levelIdx].description}
//           </div>
//           <div className="flex flex-col items-start" ref={sliderRef}>
//             <div
//               className="relative"
//               style={{ width: sliderWidth, height: "82.5px", marginBottom: "16px" }}
//             >
//               <img
//                 src={sliderLineImg}
//                 alt="slider-bar"
//                 style={{
//                   width: sliderWidth,
//                   height: "6px",
//                   position: "absolute",
//                   left: 0,
//                   top: "40px",
//                   zIndex: 1,
//                   opacity: 1,
//                 }}
//                 draggable={false}
//               />
//               <img
//                 src={knobImg}
//                 alt="slider-knob"
//                 className={isDragging ? "cursor-grabbing" : "cursor-pointer"}
//                 style={{
//                   position: "absolute",
//                   top: "32px",
//                   left: KNOB_OFFSET_PX + levelIdx * STEP,
//                   width: knobWidth,
//                   zIndex: 2,
//                   opacity: 1,
//                   transition: isDragging
//                     ? "none"
//                     : "left 0.3s cubic-bezier(.4,1.7,.5,1)",
//                   userSelect: "none",
//                   cursor: isDragging ? "grabbing" : "pointer",
//                   touchAction: "none",
//                 }}
//                 draggable={false}
//                 onMouseDown={onKnobDown}
//                 onTouchStart={onKnobDown}
//               />
//               <div
//                 className="absolute left-0 w-full flex flex-row justify-between"
//                 style={{ top: 60 }}
//               >
//                 {[1, 2, 3, 4, 5].map((lvl, idx) => (
//                   <div
//                     key={lvl}
//                     className="flex flex-col items-center"
//                     style={{ width: 110 }}
//                   >
//                     {levelIdx === idx ? (
//                       <>
//                         <span
//                           className="text-[#141414] font-medium text-[38px] uppercase block leading-[135%]"
//                           style={{
//                             fontFamily: "Gotham, sans-serif",
//                             letterSpacing: 0,
//                             textAlign: "center",
//                           }}
//                         >
//                           {`0${lvl}`}
//                         </span>
//                         <span
//                           className="text-[#141414] font-medium text-[15px] uppercase block leading-[135%]"
//                           style={{
//                             fontFamily: "Gotham, sans-serif",
//                             letterSpacing: 0,
//                             textAlign: "center",
//                           }}
//                         >
//                           LEVEL
//                         </span>
//                       </>
//                     ) : (
//                       <span
//                         className="text-[#9F9E9E] font-medium text-[19px] uppercase block leading-[135%] flex gap-1"
//                         style={{
//                           fontFamily: "Gotham, sans-serif",
//                           letterSpacing: 0,
//                           textAlign: "center",
//                           alignItems: "center",
//                           justifyContent: "center",
//                         }}
//                       >
//                         {`0${lvl}`} <span className="ml-1">LEVEL</span>
//                       </span>
//                     )}
//                   </div>
//                 ))}
//               </div>
//             </div>
//           </div>
//           <div className="relative mt-16 select-none">
//             <img
//               src={groupImg}
//               alt="people-table"
//               className="absolute top-0 left-12 pointer-events-none user-drag-none"
//               style={{
//                 width: "650px",
//                 height: "300px",
//                 opacity: 1,
//                 pointerEvents: "none",
//                 userSelect: "none",
//               }}
//               draggable={false}
//             />

//             {/* Gradient overlay with border */}
//             <div
//               style={{
//                 position: "absolute",
//                 left: 0,
//                 top: 0,
//                 width: "100%",
//                 height: "300px",
//                 pointerEvents: "none",
//                 background:
//                   "linear-gradient(180deg,rgba(255,255,255,0) 0%, #FFFFFF 100%)",
//               }}
//             />

//             <button
//               type="button"
//               onClick={handleContinue}
//               className="absolute flex items-center justify-between font-medium bg-black text-white text-[22px] rounded-[20.66px] shadow-md"
//               style={{
//                 width: "250.57px",
//                 height: "70.94px",
//                 top: "106.73px",
//                 left: "220px",
//                 padding: "22.72px 20.66px",
//                 fontFamily: "Gotham, sans-serif",
//                 letterSpacing: 0,
//                 opacity: 1,
//                 zIndex: 3,
//               }}
//             >
//               Make Selection
//               <span className="ml-4 flex items-center">
//                 <svg width={22} height={22} viewBox="0 0 22 22" fill="none">
//                   <circle cx="11" cy="11" r="11" fill="#fff" />
//                   <path
//                     d="M9 7l4 4-4 4"
//                     stroke="#151A1F"
//                     strokeWidth="2"
//                     strokeLinecap="round"
//                     strokeLinejoin="round"
//                   />
//                 </svg>
//               </span>
//             </button>
//           </div>
//         </div>
//         {/* RIGHT: Recommendation Card */}
//         <div className="flex flex-col">
//           <RecommendedPositionCard card={LEVELS[levelIdx].card} />
//         </div>
//       </div>
//     </div>
//   );
// }
